(() => {
  if (customElements.get('vital-price-range')) return;

  class VitalPriceRange extends HTMLElement {
    connectedCallback() {
      this.minRange = this.querySelector('[data-vf-price-min]');
      this.maxRange = this.querySelector('[data-vf-price-max]');
      this.minHidden = this.querySelector('[data-vf-price-min-hidden]');
      this.maxHidden = this.querySelector('[data-vf-price-max-hidden]');
      this.output = this.querySelector('[data-vf-price-output]');
      this.maxValue = Number(this.dataset.max || this.maxRange?.max || 0);

      if (!this.minRange || !this.maxRange || !this.maxValue) return;

      this.onInput = this.onInput.bind(this);
      this.onChange = this.onChange.bind(this);

      this.minRange.addEventListener('input', this.onInput);
      this.maxRange.addEventListener('input', this.onInput);
      this.minRange.addEventListener('change', this.onChange);
      this.maxRange.addEventListener('change', this.onChange);

      this.update();
    }

    onInput(event) {
      // Keep Shopify from refreshing while the handle is still being dragged.
      event.stopPropagation();

      let min = Math.round(Number(this.minRange.value));
      let max = Math.round(Number(this.maxRange.value));

      if (event.target === this.minRange && min > max) {
        min = max;
        this.minRange.value = String(max);
      }

      if (event.target === this.maxRange && max < min) {
        max = min;
        this.maxRange.value = String(min);
      }

      this.update();
    }

    onChange(event) {
      // Prevent the range input itself from reaching Dawn's generic facet listener.
      event.stopPropagation();

      let min = Math.round(Number(this.minRange.value));
      let max = Math.round(Number(this.maxRange.value));

      if (min > max) {
        if (event.target === this.minRange) {
          min = max;
          this.minRange.value = String(max);
        } else {
          max = min;
          this.maxRange.value = String(min);
        }
      }

      this.update();

      // Trigger Shopify's own AJAX facet refresh through the hidden field.
      // The form serializes BOTH price values, updates the product grid in place,
      // and pushes the filter values into the URL without reloading the page.
      const trigger = event.target === this.minRange ? this.minHidden : this.maxHidden;
      if (trigger) {
        window.requestAnimationFrame(() => {
          trigger.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }
    }

    resetRange() {
      this.minRange.value = '0';
      this.maxRange.value = String(this.maxValue);
      this.update();
    }

    format(value) {
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const currency = this.dataset.currency || 'EUR';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      } catch (_) {
        return '€' + Math.round(value);
      }
    }

    update() {
      const min = Number(this.minRange.value);
      const max = Number(this.maxRange.value);
      const minPct = Math.max(0, Math.min(100, (min / this.maxValue) * 100));
      const maxPct = Math.max(0, Math.min(100, (max / this.maxValue) * 100));

      this.style.setProperty('--vf-price-min', minPct + '%');
      this.style.setProperty('--vf-price-max', maxPct + '%');

      if (this.minHidden) {
        this.minHidden.value = min > 0 ? String(min) : '';
        this.minHidden.disabled = min <= 0;
      }

      if (this.maxHidden) {
        this.maxHidden.value = max < this.maxValue ? String(max) : '';
        this.maxHidden.disabled = max >= this.maxValue;
      }

      if (this.output) {
        this.output.textContent = this.format(min) + ' – ' + this.format(max);
      }
    }
  }

  customElements.define('vital-price-range', VitalPriceRange);
})();